import type { ShellBffClient, ShellBffClientConfig, ShellBffQuery, ShellBffRequest } from './types'
import { ShellBffHttpError, ShellBffNetworkError } from './errors'

const trimSlashes = (value: string): string => value.replace(/(^\/+)|(\/+$)/g, '')

const normalizePath = (value: string): string => (value.startsWith('/') ? value : `/${value}`)

function isJsonLike(value: unknown): value is Record<string, unknown> | unknown[] {
  if (value === null || typeof value !== 'object') {
    return false
  }

  return !(value instanceof FormData) && !(value instanceof URLSearchParams) && !(value instanceof Blob)
}

function appendQuery(url: URL, query?: ShellBffQuery): void {
  if (!query) {
    return
  }

  Object.entries(query).forEach(([key, rawValue]) => {
    const values = Array.isArray(rawValue) ? rawValue : [rawValue]

    values.forEach((value) => {
      if (value === null || value === undefined) {
        return
      }

      url.searchParams.append(key, String(value))
    })
  })
}

function mergeHeaders(...parts: Array<HeadersInit | undefined>): Headers {
  const merged = new Headers()

  parts.filter(Boolean).forEach((headersPart) => {
    const headers = new Headers(headersPart)
    headers.forEach((value, key) => {
      merged.set(key, value)
    })
  })

  return merged
}

function buildUrl(baseUrl: string | undefined, resolvedPath: string): URL {
  const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin

  if (/^https?:\/\//i.test(resolvedPath)) {
    return new URL(resolvedPath)
  }

  if (!baseUrl) {
    return new URL(normalizePath(resolvedPath), origin)
  }

  if (/^https?:\/\//i.test(baseUrl)) {
    const base = `${trimSlashes(baseUrl)}`
    return new URL(normalizePath(resolvedPath), `${base}/`)
  }

  const prefixedPath = `${normalizePath(baseUrl)}${normalizePath(resolvedPath)}`
  return new URL(prefixedPath, origin)
}

async function readResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    return response.json()
  }

  return response.text()
}

export function createShellBffClient(config: ShellBffClientConfig): ShellBffClient {
  const fetchFn = config.fetchFn ?? fetch

  const request = async <TResponse = unknown>(input: ShellBffRequest): Promise<TResponse> => {
    const method = (input.method ?? 'GET').toUpperCase()
    const resolvedPath = config.resolvePath(input.child, input.path)
    const url = buildUrl(config.baseUrl, resolvedPath)
    appendQuery(url, input.query)

    const dynamicHeaders = await config.getHeaders?.()
    const headers = mergeHeaders(config.defaultHeaders, dynamicHeaders, input.headers)

    const requestInit: RequestInit = {
      method,
      headers,
      signal: input.signal,
      credentials: input.credentials ?? 'include',
    }

    if (input.body !== undefined) {
      if (isJsonLike(input.body)) {
        if (!headers.has('content-type')) {
          headers.set('content-type', 'application/json')
        }

        requestInit.body = JSON.stringify(input.body)
      }
      else {
        requestInit.body = input.body as BodyInit
      }
    }

    try {
      const response = await fetchFn(url.toString(), requestInit)
      const responseBody = await readResponseBody(response)

      if (!response.ok) {
        throw new ShellBffHttpError(
          `Shell BFF request failed with status ${response.status} ${url.toString()}`,
          response.status,
          method,
          url.toString(),
          responseBody,
        )
      }

      return responseBody as TResponse
    }
    catch (error) {
      if (error instanceof ShellBffHttpError) {
        throw error
      }

      throw new ShellBffNetworkError('Shell BFF network request failed', error)
    }
  }

  return {
    request,
  }
}
